import { z } from 'zod';
import { paginationQuery } from './common.js';
export const receptionUploadInput = z.object({
    xmlBase64: z.string().min(50, 'Archivo XML inválido').max(4_000_000, 'El XML es demasiado grande'),
    pdfBase64: z.string().max(12_000_000, 'El PDF es demasiado grande').nullable().optional(),
});
export const receptionDecisionInput = z
    .object({
    decision: z.enum(['1', '2', '3']),
    detail: z.string().trim().max(160).nullable().optional(),
    taxCondition: z
        .string()
        .regex(/^0[1-5]$/)
        .default('01'),
    creditableTax: z.coerce.number().min(0).nullable().optional(),
    applicableExpense: z.coerce.number().min(0).nullable().optional(),
    expenseCategoryId: z.uuid().nullable().optional(),
    /** Crea el gasto correspondiente (no aplica a rechazos). */
    createExpense: z.boolean().default(true),
    /**
     * Módulo Proyectos: imputa el 100 % del gasto al proyecto (y fase). En una nota de crédito aceptada registra un
     * gasto negativo imputado al proyecto. Requiere el permiso projects:allocate.
     */
    projectId: z.uuid().nullable().optional(),
    phaseId: z.uuid().nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.decision !== '1' && !v.detail) {
        ctx.addIssue({ code: 'custom', path: ['detail'], message: 'Explicá el motivo al proveedor' });
    }
});
/** Respuesta masiva ("Procesar múltiples"): una sola decisión para varios comprobantes pendientes. */
export const receptionBulkDecisionInput = z
    .object({
    ids: z.array(z.uuid()).min(1, 'Elegí al menos un comprobante').max(100, 'Máximo 100 por lote'),
    decision: z.enum(['1', '2', '3']),
    detail: z.string().trim().max(160).nullable().optional(),
    taxCondition: z
        .string()
        .regex(/^0[1-5]$/)
        .default('01'),
    expenseCategoryId: z.uuid().nullable().optional(),
    createExpense: z.boolean().default(true),
    projectId: z.uuid().nullable().optional(),
    phaseId: z.uuid().nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.decision !== '1' && !v.detail) {
        ctx.addIssue({ code: 'custom', path: ['detail'], message: 'Explicá el motivo al proveedor' });
    }
});
export const receptionListQuery = paginationQuery.extend({
    status: z.enum(['pending', 'accepted', 'partial', 'rejected', 'error']).optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    /** Módulo Proyectos: aceptados cuyo gasto no está (del todo) imputado a proyectos. */
    allocation: z.enum(['unassigned']).optional(),
});
/* ---------------------------- gastos ---------------------------- */
export const expenseInput = z
    .object({
    date: z.iso.date('Fecha inválida'),
    description: z.string().trim().min(2, 'Requerido').max(200),
    supplierName: z.string().trim().max(160).nullable().optional(),
    amount: z.coerce.number().positive('Mayor a cero'),
    taxAmount: z.coerce.number().min(0).default(0),
    currency: z.enum(['CRC', 'USD', 'EUR']).default('CRC'),
    exchangeRate: z.coerce.number().positive().nullable().optional(),
    categoryId: z.uuid().nullable().optional(),
    paymentMethod: z
        .string()
        .regex(/^\d{2}$/)
        .default('04'),
    reference: z.string().trim().max(80).nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    /**
     * Módulo Proyectos: imputa el 100 % del gasto al proyecto (y fase). Al editar, `undefined` no toca las
     * asignaciones y `null` quita la asignación completa. Requiere el permiso projects:allocate.
     */
    projectId: z.uuid().nullable().optional(),
    phaseId: z.uuid().nullable().optional(),
    /** Gasto de la empresa: no se imputa a una obra. */
    forCompany: z.boolean().default(false),
    costGroupId: z.uuid().nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.forCompany && v.projectId) {
        ctx.addIssue({
            code: 'custom',
            path: ['projectId'],
            message: 'Un gasto de la empresa no se imputa a un proyecto',
        });
    }
});
export const expenseListQuery = paginationQuery.extend({
    categoryId: z.uuid().optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    source: z.enum(['manual', 'reception']).optional(),
    /** Módulo Proyectos: id del proyecto, o `none` para los gastos sin imputar. */
    projectId: z.union([z.uuid(), z.literal('none')]).optional(),
});
/** Límite de cada adjunto de un gasto (10 MB) y tipos admitidos. */
export const EXPENSE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const EXPENSE_ATTACHMENT_TYPES = [
    'application/pdf',
    'application/xml',
    'text/xml',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
];
export const expenseAttachmentInput = z.object({
    fileName: z.string().trim().min(1).max(200),
    contentType: z.string().trim().toLowerCase().max(100),
    /** Contenido del archivo en base64 (hasta 10 MB ya decodificado). */
    contentBase64: z.string().min(1),
});
export const expenseCategoryInput = z.object({
    name: z.string().trim().min(2).max(60),
    color: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .default('#6b6af0'),
    costGroupId: z.uuid().nullable().optional(),
});
/* ---------------------------- buzón IMAP ---------------------------- */
export const inboxSettingsInput = z.object({
    enabled: z.boolean().default(true),
    host: z.string().trim().min(3).max(200),
    port: z.coerce.number().int().min(1).max(65535).default(993),
    secure: z.boolean().default(true),
    username: z.string().trim().min(3).max(200),
    password: z.string().max(300).optional(),
    folder: z.string().trim().max(120).default('INBOX'),
});
//# sourceMappingURL=receptions.js.map